import React, {useEffect} from "react";
import "./App.css";
import {BrowserRouter as Router, Redirect, Route, Switch} from "react-router-dom";
import Login from "./components/auth/Login";
import NotFound from "./components/NotFound";
import Register from "./components/auth/Register";
import {LanguageContextProvider} from "./components/context/LanguageContext";
import {connect} from "react-redux";
import Authenticator from "./components/auth/Authenticator";
import {addSong, loadingUser, logOut, sendPasswordConfirmation, sendRegistrationConfirmation, setJwtToken, setPortal, setUser} from "./store/actions/Actions"
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import SpotifyCallback from "./components/auth/SpotifyCallback";
import Landing from "./components/marketing/Landing";
import {loadAuth} from "./libraries/Authentication";
import Home from "./components/layouts/Home";
import axios from "axios";
import * as config from "./configs/Config";
import {MusicKitInit} from "./libraries/MusicKit";
import {APP_ORIGIN, isAdminHost, isLandingHost} from "./configs/Hosts";
import AdminApp from "./components/admin/AdminApp";

if (process.env.NODE_ENV === "production") {
    console.log = function no_console() {
    };
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function configureMusicKit() {
    let attempt = 0;
    const maxTry = 20;
    while (!window.MusicKit && attempt < maxTry) {
        await sleep(500);
        attempt++;
    }

    if (!window.MusicKit) {
        console.error('Problem with musickit, try to reload the page');
        return;
    }

    try {
        const {data} = await axios.get(`${config.API_URL}/musickit/token`, {
            headers: {Accept: 'application/json'},
        });
        await window.MusicKit.configure({
            app: {
                name: 'AbTunes',
                build: '1978.4.1',
            },
            developerToken: data.token,
        });
        MusicKitInit();
        window.dispatchEvent(new Event('musickitconfigured'));
    } catch (err) {
        console.error('Failed to configure MusicKit:', err);
    }
}

configureMusicKit();


function App({
                 user, auth, setJwtToken, setUser, setPortal, sendPasswordConfirmation, sendRegistrationConfirmation,
                 logOut, songsCount, loadingUser, addSong, portal
             }) {

    useEffect(() => {
        //console.log('Document ready, initializing...');
    }, []);

    const RenderAuthenticator = ({children}) => (
        <Authenticator user={user} setJwtToken={setJwtToken} setPortal={setPortal} setUser={setUser} loadingUser={loadingUser}>
            {children}
        </Authenticator>
    )


    // FIXME replace LanguageContextProvider with react-intl
    // The landing domain only serves "/": app routes (login, reset links...)
    // move to the app domain, keeping path and query.
    const {pathname, search, hash} = window.location;
    if (isLandingHost() && pathname !== '/' && pathname !== '/welcome') {
        window.location.replace(APP_ORIGIN + pathname + search + hash);
        return null;
    }

    return (
        <Router>
            <LanguageContextProvider>
                <Switch>
                    <Route path="/" exact>
                        {isLandingHost() ? (
                            <Landing/>
                        ) : isAdminHost() ? (
                            <RenderAuthenticator>
                                <AdminApp/>
                            </RenderAuthenticator>
                        ) : loadAuth() ? (
                            <RenderAuthenticator>
                                <Home user={user} logout={logOut}/>
                            </RenderAuthenticator>
                        ) : (
                            <Redirect to="/login"/>
                        )}
                    </Route>
                    <Route path="/welcome" exact>
                        <Landing/>
                    </Route>
                    <Route path="/login" exact>
                        <RenderAuthenticator>
                            <Login user={user} auth={auth} setUser={setUser} setJwtToken={setJwtToken} setPortal={setPortal}/>
                        </RenderAuthenticator>
                    </Route>
                    <Route path="/register" exact>
                        <RenderAuthenticator>
                            <Register/>
                        </RenderAuthenticator>
                    </Route>
                    <Route path="/forgot-password" exact>
                        <ForgotPassword/>
                    </Route>
                    <Route path="/reset-password" exact>
                        <ResetPassword/>
                    </Route>
                    {/* OAuth landing — outside the authenticator so the code
                        exchange can run before any session exists. */}
                    <Route path="/spotify-callback" exact>
                        <SpotifyCallback/>
                    </Route>
                    {/* The admin: everything else on admin.abtunes.com, or /admin
                        on the app host (local development). */}
                    <Route path={isAdminHost() ? '/' : '/admin'}>
                        <RenderAuthenticator>
                            <AdminApp base={isAdminHost() ? '' : '/admin'}/>
                        </RenderAuthenticator>
                    </Route>
                    <Route component={NotFound}/>
                </Switch>
            </LanguageContextProvider>
        </Router>
    );
}

export default connect(state => {
    const isFirstTimeForUser = () => {
        if (!state.user.isLoaded) return false

        const {currentUser} = state.user
        if (currentUser && currentUser.attributes && currentUser.attributes.uploadsAvailable > 0) {
            return false
        }

        if (!state.songs.loaded) return false
        const {songs} = state.songs

        if (songs.length > 0) return false

        return true
    }

    return ({
        user: state.user,
        auth: state.auth,
        portal: state.portal,
        firstTime: isFirstTimeForUser()
    })
}, {setJwtToken, setUser, sendPasswordConfirmation, logOut, setPortal, loadingUser, addSong, sendRegistrationConfirmation})(App);
